import { useCallback } from 'react'
import { useAtom, useSetAtom } from 'jotai'
import { Box, Button, Grid, Dialog, DialogContent, Typography, Divider, Link, styled } from '@mui/material'
import { ArrowRight as ArrowRightIcon } from '@mui/icons-material'

import { DialogHeader } from '../../components/ui'
import { RequestTrackingByOrderResponse, RequestTrackingByChildrenResponse } from '../../types/tracking'
import { CourierProps } from '../../types/default'
import { formatDate } from '../../utils/format'
import { findCourier } from '../../utils/utilities'
import { childrenOrdersOpenState } from '../../state'

import { StepIconEvent, StepLabelEvent } from './timeline'
import { TrackingAlertErrorByOrder } from './alert'

import DEFAULT from '../../config/default'

interface ChildrenOrdersProps {
    children?: RequestTrackingByChildrenResponse,
    courier?: CourierProps
}

const TrackingChildrenOrders = ({ children, courier }: ChildrenOrdersProps) => {
    const [open, setOpen] = useAtom(childrenOrdersOpenState)

    const findValues = useCallback((idEventCategory: number) => {

        let position = 0
        let status = 2

        if ([1, 3, 5, 7].includes(idEventCategory)) { // success
            status = 1
        }

        if ([1, 2].includes(idEventCategory)) {
            position = 1
        } else if ([3, 4].includes(idEventCategory)) {
            position = 2
        } else if ([5, 6].includes(idEventCategory)) {
            position = 3
        } else if ([7, 8].includes(idEventCategory)) {
            position = 4
        }

        return [position, status]
    }, [])

    return (
        children ? (
            <Dialog open={open} maxWidth="md" onClose={() => setOpen(false)}>
                <DialogHeader id="order-status-list" onClose={() => setOpen(false)}>
                    <span>
                        Estado del pedido
                    </span>
                </DialogHeader>
                <DialogContent dividers>
                    {(children && children.listPedido.length > 0) ? (
                        children.listPedido.map((order, index) => {
                            const values = findValues(order.idCategoriaEvento)
                            const tracking = findCourier(order, false)
                            return (
                                <Box key={`children-order-${ index }`}>
                                    <Grid container spacing={2} alignItems="center">
                                        <Grid item xl={6} lg={6} md={6} sm={6} xs={12}>
                                            <InfoStyled>
                                                <Typography variant="body2">
                                                    <strong>Caja</strong> {index+1}/{children.listPedido.length}
                                                </Typography>
                                                <Typography variant="body2">
                                                    <strong>Nro. de envío</strong> {order.numeroGuia || 'no disponilbe'}
                                                </Typography>
                                                { order.fechaEvento && (
                                                    <Typography variant="caption">{formatDate(order.fechaEvento, 'dddd DD.MM.YYYY | HH:mm [hs.]')}</Typography>
                                                )}
                                                { (tracking && tracking.urlToTracking) && (
                                                    <LinkContainerStyled>
                                                        <Link href={tracking.urlToTracking} target="_blank" variant="body2" underline="hover" sx={{ display: 'flex' }}>
                                                            <ArrowRightIcon fontSize="small"/> Click aquí para seguir la caja
                                                        </Link>
                                                    </LinkContainerStyled>
                                                )}
                                            </InfoStyled>
                                            {/* <InfoStyled>
                                                <TrackingAlertErrorByOrder order={order} />
                                            </InfoStyled> */}
                                        </Grid>
                                        <Grid item xl={6} lg={6} md={6} sm={12} xs={12}>
                                            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center'}}>
                                                <StepIconEvent
                                                    icon={`${ values[0] }-${ values[1] }`}
                                                />
                                                <StepLabelEvent
                                                    name={order.nombreVisualizacionConsultoraEvento}
                                                    status={values[1]}
                                                    gutterBottom={false} 
                                                />
                                            </Box>
                                        </Grid>  
                                        <Grid item xl={12} lg={12} md={12} sm={12} xs={12}>
                                            <MessageStyled>
                                                <TrackingAlertErrorByOrder order={order} />
                                            </MessageStyled>
                                        </Grid>    
                                    </Grid>
                                    { index < children.listPedido.length - 1 && (
                                        <Box sx={{ my: 2 }}>
                                            <Divider />
                                        </Box>
                                    )}
                                </Box>
                            )
                        })
                    ) : (
                        <div>no results</div>
                    )}
                </DialogContent>
            </Dialog>
        ) : null
    )
}

interface CourierLinkProps {
    data: RequestTrackingByOrderResponse,
    courier?: CourierProps
}

const CourierLink = ({ courier, data }: CourierLinkProps) => {    
    const setOpen = useSetAtom(childrenOrdersOpenState)
    const isGuideVisible = (DEFAULT.api.countryCode === 'MX' && courier?.name === 'dhl') || (DEFAULT.api.countryCode === 'CO')
    return (
        <GroupStyled>
            { isGuideVisible && (
                <NumberStyled>
                    <Typography  noWrap title={data.pedido.numeroGuia ?? ''}>
                        Guia: {data.pedido.numeroGuia ?? "-"}
                    </Typography>
                </NumberStyled>
            )}
            {(courier && courier.isMultibulto) ? (
                <Button
                    size="large"
                    fullWidth
                    onClick={() => setOpen(true)}
                >
                    Ver más detalles de tu envío
                </Button>
            ) : (
                <Button
                    disabled={!courier || (courier && !courier.urlToTracking) || data.pedido.idEvento === 0}
                    size="large"
                    href={(courier && courier.urlToTracking) ? courier.urlToTracking : ''}
                    target="_blank"
                    fullWidth
                >
                    Ver más detalles de tu envío
                </Button>
            )}
        </GroupStyled>
    )
}

const GroupStyled = styled(Box)(({ theme }) => ({
    display: 'flex',
    [theme.breakpoints.down('md')]: {
        flexDirection: 'column'
    }
}))

const NumberStyled = styled(Typography)(({ theme }) => ({
    minWidth: 200,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: theme.spacing(2),
    paddingTop: theme.spacing(.5),
    paddingBottom: theme.spacing(.5),
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2),
    background: theme.palette.grey[200],
    borderRadius: theme.spacing(.5),
    [theme.breakpoints.down('md')]: {
        marginRight: 0,
        marginBottom: theme.spacing(1),
        height: 42,
        justifyContent: 'center'
    }
}))

const InfoStyled = styled(Box)(({ theme }) => ({
    minWidth: 250,
    [theme.breakpoints.down('md')]: {
        minWidth: 'auto',
        textAlign: 'center'
    }
}))

const MessageStyled = styled(Box)(({ theme }) => ({
    // minWidth: 420,
    // maxWidth: 420,
    // [theme.breakpoints.down('md')]: {
    //     minWidth: 'auto',
    //     textAlign: 'center'
    // }
}))

const LinkContainerStyled = styled(Box)(({ theme }) => ({
    marginTop: theme.spacing(1),
    [theme.breakpoints.down('md')]: {
        display: 'flex',
        justifyContent: 'center'
    }
}))

// const AlertStyled = styled(Box)(({ theme }) => ({
//     minWidth: 350,
//     [theme.breakpoints.down('md')]: {
//         minWidth: 'auto',
//         textAlign: 'center'
//     }
// }))

export { TrackingChildrenOrders, CourierLink }
